import React, { useEffect, useState } from 'react';
import {
  Card, Input, Button, Select, Typography, Space, message, Spin, InputNumber,
  Divider, Tag, Empty, Tooltip,
} from 'antd';
import {
  PlusOutlined, MailOutlined, ClockCircleOutlined, TagsOutlined, UnorderedListOutlined,
  StopOutlined, ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, EditOutlined,
  ThunderboltOutlined, SaveOutlined, PlayCircleOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import EmailBuilderModal from '../components/EmailBuilderModal';
import ListSelect from '../components/ListSelect';

const { Title, Text } = Typography;

const STEP_META = {
  send_email:       { label: 'Send email',       icon: <MailOutlined />,         color: '#6366f1' },
  wait:             { label: 'Wait',             icon: <ClockCircleOutlined />,  color: '#f59e0b' },
  add_tag:          { label: 'Add tag',          icon: <TagsOutlined />,         color: '#10b981' },
  remove_tag:       { label: 'Remove tag',       icon: <TagsOutlined />,         color: '#ef4444' },
  add_to_list:      { label: 'Add to list',      icon: <UnorderedListOutlined />, color: '#0ea5e9' },
  remove_from_list: { label: 'Remove from list', icon: <UnorderedListOutlined />, color: '#ef4444' },
  exit:             { label: 'Exit automation',  icon: <StopOutlined />,         color: '#6b7280' },
};

const TRIGGER_OPTIONS = [
  { value: 'list_join',           label: 'When a subscriber joins a list' },
  { value: 'tag_added',           label: 'When a tag is added to a subscriber' },
  { value: 'manual',              label: 'Manual enrollment only' },
  { value: 'automation_finished', label: 'After a subscriber finishes another automation' },
  { value: 'campaign_finished',   label: 'After a campaign completes' },
];

export default function CreateAutomation() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading]   = useState(isEdit);
  const [saving, setSaving]     = useState(false);
  const [name, setName]         = useState('');
  const [triggerType, setTriggerType] = useState('list_join');
  const [triggerConfig, setTriggerConfig] = useState({});
  const [steps, setSteps]       = useState([]);
  const [status, setStatus]     = useState('draft');

  const [lists, setLists]       = useState([]);
  const [automations, setAutomations] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);

  // Load reference data + (if editing) the automation
  useEffect(() => {
    (async () => {
      try {
        const [listRes, autoRes, campRes] = await Promise.allSettled([
          axios.get('/api/lists'),
          axios.get('/api/automations'),
          axios.get('/api/campaigns?page=1&limit=100'),
        ]);
        if (listRes.status === 'fulfilled') setLists(Array.isArray(listRes.value.data) ? listRes.value.data : (listRes.value.data?.data || []));
        if (autoRes.status === 'fulfilled') setAutomations((autoRes.value.data || []).filter(a => String(a.id) !== String(id)));
        if (campRes.status === 'fulfilled') setCampaigns(campRes.value.data?.data || campRes.value.data || []);

        if (isEdit) {
          const res = await axios.get(`/api/automations/${id}`);
          setName(res.data.name || '');
          setTriggerType(res.data.trigger_type || 'list_join');
          setTriggerConfig(res.data.trigger_config || {});
          setStatus(res.data.status || 'draft');
          setSteps((res.data.steps || []).map(s => ({ type: s.type, config: s.config || {} })));
        }
      } catch (e) {
        message.error('Failed to load automation');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  // ── Step manipulation ──────────────────────────────────────────────
  const addStep = (type) => {
    const config = type === 'wait' ? { amount: 1, unit: 'days' } : {};
    setSteps([...steps, { type, config }]);
    if (type === 'send_email') { setEditingIdx(steps.length); setEditorOpen(true); }
  };
  const updateStep = (idx, config) => setSteps(steps.map((s, i) => i === idx ? { ...s, config } : s));
  const removeStep = (idx) => setSteps(steps.filter((_, i) => i !== idx));
  const moveStep = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[idx], next[j]] = [next[j], next[idx]];
    setSteps(next);
  };

  // ── Persistence ─────────────────────────────────────────────────────
  const persist = async () => {
    if (!name.trim()) { message.warning('Give your automation a name'); return null; }
    setSaving(true);
    try {
      let automationId = id;
      const payload = { name: name.trim(), trigger_type: triggerType, trigger_config: triggerConfig };
      if (isEdit) {
        await axios.put(`/api/automations/${id}`, payload);
      } else {
        const res = await axios.post('/api/automations', payload);
        automationId = res.data.id;
      }
      await axios.put(`/api/automations/${automationId}/steps`, { steps });
      return automationId;
    } catch (e) {
      message.error(e.response?.data?.message || 'Failed to save');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const aid = await persist();
    if (aid) { message.success('Saved'); if (!isEdit) navigate(`/automations/${aid}/edit`, { replace: true }); }
  };

  const handleActivate = async () => {
    if (steps.length === 0) { message.warning('Add at least one step'); return; }
    const aid = await persist();
    if (!aid) return;
    try {
      await axios.post(`/api/automations/${aid}/activate`);
      message.success('Automation activated 🎉');
      setStatus('active');
    } catch (e) {
      message.error(e.response?.data?.message || 'Could not activate');
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/automations')}>Back</Button>
        {status !== 'draft' && <Tag color={status === 'active' ? 'success' : 'warning'}>{status}</Tag>}
      </Space>

      {/* Name + Trigger */}
      <Card title={<Space><ThunderboltOutlined />Trigger</Space>} style={{ borderRadius: 14, marginBottom: 20 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>Name</Text>
        <Input
          placeholder="e.g. Welcome series"
          value={name}
          onChange={e => setName(e.target.value)}
          style={{ marginBottom: 16, marginTop: 4 }}
        />
        <Text type="secondary" style={{ fontSize: 13 }}>Enroll subscribers…</Text>
        <Select
          style={{ width: '100%', marginTop: 4 }}
          value={triggerType}
          onChange={(v) => { setTriggerType(v); setTriggerConfig({}); }}
          options={TRIGGER_OPTIONS}
        />

        {/* Contextual trigger config */}
        {triggerType === 'list_join' && (
          <div style={{ marginTop: 12 }}>
            <ListSelect lists={lists} setLists={setLists} value={triggerConfig.list_id} onChange={(v) => setTriggerConfig({ list_id: v })} />
          </div>
        )}
        {triggerType === 'tag_added' && (
          <Input
            style={{ marginTop: 12 }} placeholder="Tag name (e.g. vip)"
            value={triggerConfig.tag} onChange={(e) => setTriggerConfig({ tag: e.target.value })}
          />
        )}
        {triggerType === 'automation_finished' && (
          <Select
            style={{ width: '100%', marginTop: 12 }} placeholder="Select source automation"
            value={triggerConfig.source_automation_id} onChange={(v) => setTriggerConfig({ source_automation_id: v })}
            options={automations.map(a => ({ value: a.id, label: a.name }))}
          />
        )}
        {triggerType === 'campaign_finished' && (
          <Select
            style={{ width: '100%', marginTop: 12 }} placeholder="Select source campaign"
            value={triggerConfig.source_campaign_id} onChange={(v) => setTriggerConfig({ source_campaign_id: v })}
            options={campaigns.map(c => ({ value: c.id, label: c.name }))}
          />
        )}
      </Card>

      {/* Steps */}
      <Card title="Steps" style={{ borderRadius: 14, marginBottom: 20 }}>
        {steps.length === 0 && (
          <Empty description="No steps yet — add the first action below." image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
        <Space direction="vertical" style={{ width: '100%' }} size={10}>
          {steps.map((step, idx) => {
            const meta = STEP_META[step.type];
            return (
              <Card key={idx} size="small" style={{ borderRadius: 10, borderLeft: `3px solid ${meta.color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <Space>
                    <span style={{ color: meta.color }}>{meta.icon}</span>
                    <Text strong>{meta.label}</Text>
                  </Space>
                  <Space size={2}>
                    <Button type="text" size="small" icon={<ArrowUpOutlined />} disabled={idx === 0} onClick={() => moveStep(idx, -1)} />
                    <Button type="text" size="small" icon={<ArrowDownOutlined />} disabled={idx === steps.length - 1} onClick={() => moveStep(idx, 1)} />
                    {step.type === 'send_email' && (
                      <Tooltip title="Edit email"><Button type="text" size="small" icon={<EditOutlined />} onClick={() => { setEditingIdx(idx); setEditorOpen(true); }} /></Tooltip>
                    )}
                    <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => removeStep(idx)} />
                  </Space>
                </div>

                {/* Inline config per step type */}
                {step.type === 'send_email' && (
                  <div style={{ marginTop: 8 }}>
                    <Input
                      placeholder="Subject line" value={step.config.subject || ''}
                      onChange={(e) => updateStep(idx, { ...step.config, subject: e.target.value })}
                    />
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                      {step.config.html_content ? '✓ Email designed' : 'No content yet — click edit to design'}
                    </Text>
                  </div>
                )}
                {step.type === 'wait' && (
                  <Space style={{ marginTop: 8 }}>
                    <InputNumber min={1} value={step.config.amount || 1} onChange={(v) => updateStep(idx, { ...step.config, amount: v })} />
                    <Select
                      style={{ width: 120 }} value={step.config.unit || 'days'}
                      onChange={(v) => updateStep(idx, { ...step.config, unit: v })}
                      options={[{ value: 'minutes', label: 'minutes' }, { value: 'hours', label: 'hours' }, { value: 'days', label: 'days' }]}
                    />
                  </Space>
                )}
                {(step.type === 'add_tag' || step.type === 'remove_tag') && (
                  <Input
                    style={{ marginTop: 8 }} placeholder="Tag name" value={step.config.tag || ''}
                    onChange={(e) => updateStep(idx, { tag: e.target.value })}
                  />
                )}
                {(step.type === 'add_to_list' || step.type === 'remove_from_list') && (
                  <div style={{ marginTop: 8 }}>
                    <ListSelect lists={lists} setLists={setLists} value={step.config.list_id} onChange={(v) => updateStep(idx, { list_id: v })} />
                  </div>
                )}
              </Card>
            );
          })}
        </Space>

        <Divider style={{ margin: '16px 0' }} />
        <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>Add a step</Text>
        <Space wrap>
          {Object.entries(STEP_META).map(([type, meta]) => (
            <Button key={type} icon={meta.icon} onClick={() => addStep(type)}>{meta.label}</Button>
          ))}
        </Space>
      </Card>

      {/* Actions */}
      <Space>
        <Button icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save draft</Button>
        <Button type="primary" icon={<PlayCircleOutlined />} loading={saving} onClick={handleActivate}>
          Save &amp; Activate
        </Button>
      </Space>

      {/* Email builder */}
      <EmailBuilderModal
        open={editorOpen}
        title={`Step ${editingIdx != null ? editingIdx + 1 : ''} — Email`}
        initialDesign={editingIdx != null && steps[editingIdx]?.config?.design_json
          ? (typeof steps[editingIdx].config.design_json === 'string'
              ? JSON.parse(steps[editingIdx].config.design_json) : steps[editingIdx].config.design_json)
          : null}
        onClose={() => setEditorOpen(false)}
        onSave={({ html, design }) => {
          if (editingIdx != null) {
            const cur = steps[editingIdx].config || {};
            updateStep(editingIdx, { ...cur, html_content: html, design_json: design ? JSON.stringify(design) : null });
          }
          setEditorOpen(false);
        }}
      />
    </div>
  );
}
